import { MenuItem } from 'components/navigation/KebabMenu';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useAddNewTimecardsAction } from 'pages/invoices/layout/views/components/AddNewTimecardsButton';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { useSelector } from 'store';
import { FeatureFlag } from 'types/features';
import { LimitType } from 'types/invoice-details';
import { InvoiceData } from 'types/invoices';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

const CORRECT_PERMISSIONS = ['CREATE.INVOICES.MATTER_ACCESS', 'CREATE.INVOICES.TYPE_CORRECTION_WITH_MATTER_ACCESS'];
const ADD_CASE_PERMISSIONS = ['UPDATE.INVOICES.ADD_MATTER_TO_INVOICE'];
const COPY_PERMISSIONS = ['UPDATE.INVOICES.COPYING'];
const CREATE_DEBIT_PERMISSIONS = ['CREATE.INVOICES.MATTER_ACCESS', 'CREATE.INVOICES.TYPE_DEBIT_WITH_MATTER_ACCESS'];
const CREDIT_PERMISSIONS = ['CREATE.INVOICES.TYPE_CREDIT_WITH_MATTER_ACCESS'];

export interface InvoiceMoreMenuArgs {
    invoice: InvoiceData;
    isInViewMode: boolean;
    isCaseAddable: boolean;
    /** Why a shared-payment copy cannot be deleted here, or null. */
    deleteBlockedReason: string | null;
    onMerge: () => void;
    onAddCase: () => void;
    onAddNewTimecards: (limit: LimitType, invoice: InvoiceData) => Promise<void>;
    onCopy: () => void;
    onPaymentDistribution: () => void;
    onCorrect: () => void;
    onCredit: () => void;
    onCreateNewDebit: () => void;
    onDelete: () => void;
}

/**
 * The invoice header's ⋯ in the new design: classic's header buttons other than History and Comments, under the
 * same gates, in the order Merge, Add case, Add new TCs, Copy, Payment distribution, Correct, Credit, Create new
 * Debit, Delete last. Correct and Credit open the new invoice at once, so their subtitle says so.
 */
export const useInvoiceMoreMenu = ({
    invoice,
    isInViewMode,
    isCaseAddable,
    deleteBlockedReason,
    onMerge,
    onAddCase,
    onAddNewTimecards,
    onCopy,
    onPaymentDistribution,
    onCorrect,
    onCredit,
    onCreateNewDebit,
    onDelete,
}: InvoiceMoreMenuArgs): MenuItem[] => {
    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const {
        isCredit,
        isSent,
        isCreditable,
        isSplittable,
        isDeletable,
        isMergeable,
        isCorrectable,
        isPartiallyCredited,
    } = useInvoice(invoice);
    const { isSharedPaymentCopy } = useSharedPayment(invoice);
    const isCorrectionFlowEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_CORRECTION_FLOW);
    const isCopyingEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_COPYING_ENABLED);
    const canCorrect = useHasPermissions(CORRECT_PERMISSIONS);
    const canAddCase = useHasPermissions(ADD_CASE_PERMISSIONS);
    const canCopy = useHasPermissions(COPY_PERMISSIONS);
    const canCreateDebit = useHasPermissions(CREATE_DEBIT_PERMISSIONS);
    const canCredit = useHasPermissions(CREDIT_PERMISSIONS);
    const addNewTimecards = useAddNewTimecardsAction({ invoice, isInViewMode, onAddNewTimecards });

    const canUserEdit = invoice.isEditable;
    const items: (MenuItem | false)[] = [
        canUserEdit && isMergeable && { handler: onMerge, title: t('invoices.merge'), disabled: buttonsDisabled },
        canUserEdit &&
            isCaseAddable &&
            canAddCase && { handler: onAddCase, title: t('invoices.add-case'), disabled: buttonsDisabled },
        !!addNewTimecards && {
            handler: addNewTimecards,
            title: t('invoices.actions.add-new-timecards'),
            disabled: buttonsDisabled,
        },
        isSplittable && canCopy && isCopyingEnabled && { handler: onCopy, title: t('copy'), disabled: buttonsDisabled },
        isSharedPaymentCopy && {
            handler: onPaymentDistribution,
            title: t('invoices.payment-distribution.button'),
            disabled: buttonsDisabled,
        },
        isCorrectable &&
            isCorrectionFlowEnabled &&
            canCorrect && {
                handler: onCorrect,
                title: t('invoices.correct'),
                subtitle: t('invoices.more-menu.correct-subtitle'),
                disabled: buttonsDisabled || isPartiallyCredited,
            },
        isCreditable &&
            canCredit && {
                handler: onCredit,
                title: t('invoices.credit'),
                subtitle: t('invoices.more-menu.credit-subtitle'),
                disabled: buttonsDisabled || invoice.hasCreditInvoice || isPartiallyCredited,
            },
        isCredit &&
            isSent &&
            !!invoice.debitInvoice &&
            canCreateDebit && {
                handler: onCreateNewDebit,
                title: t('invoices.actions.create-new-debit'),
                disabled: buttonsDisabled,
            },
        invoice.isDeletable &&
            isDeletable && {
                handler: onDelete,
                title: t('delete'),
                // The menu has no tooltip: the reason a copy cannot go is the row's own line.
                subtitle: deleteBlockedReason ?? undefined,
                variant: 'destructive',
                disabled: !!deleteBlockedReason || buttonsDisabled || isPartiallyCredited,
            },
    ];
    return items.filter((item): item is MenuItem => !!item);
};
